import React from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Skeleton,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    People,
    Category,
    Assessment,
    TrendingUp,
    EmojiEvents,
    Security,
    Refresh,
    Visibility
} from '@mui/icons-material';

const AdminStatsCards = ({ stats, loading, onRefresh, onCardClick, compact = false }) => {
    const statsConfig = [
        {
            key: 'totalUsers',
            title: 'Total Users',
            value: stats?.totalUsers || 0,
            icon: <People sx={{ fontSize: compact ? 30 : 40 }} />,
            color: '#2196f3',
            gradient: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
            route: '/admin/users',
            trend: { direction: 'up', value: '+12%' }
        },
        {
            key: 'totalClassifications',
            title: 'Classifications',
            value: stats?.totalClassifications || 0,
            icon: <Category sx={{ fontSize: compact ? 30 : 40 }} />,
            color: '#43a047',
            gradient: 'linear-gradient(135deg, #f1f8e9, #e8f5e8)',
            route: '/admin/stats',
            trend: { direction: 'up', value: '+8%' }
        },
        {
            key: 'totalBadges',
            title: 'Total Badges',
            value: stats?.totalBadges || 0,
            icon: <EmojiEvents sx={{ fontSize: compact ? 30 : 40 }} />,
            color: '#ff5722',
            gradient: 'linear-gradient(135deg, #fbe9e7, #ffccbc)',
            route: '/admin/badges',
            trend: { direction: 'up', value: '+3%' }
        },
        {
            key: 'weeklyGrowth',
            title: 'Weekly Growth',
            value: `${stats?.weeklyGrowth || 0}%`,
            icon: <TrendingUp sx={{ fontSize: compact ? 30 : 40 }} />,
            color: '#ff9800',
            gradient: 'linear-gradient(135deg, #fff8e1, #ffecb3)',
            route: '/admin/stats',
            trend: { direction: 'up', value: '+5%' }
        },
        {
            key: 'monthlyGrowth',
            title: 'Monthly Growth',
            value: `${stats?.monthlyGrowth || 0}%`,
            icon: <Assessment sx={{ fontSize: compact ? 30 : 40 }} />,
            color: '#9c27b0',
            gradient: 'linear-gradient(135deg, #f3e5f5, #e1bee7)',
            route: '/admin/stats',
            trend: { direction: 'up', value: '+15%' }
        },
        {
            key: 'avgAccuracy',
            title: 'Avg Accuracy',
            value: `${stats?.avgAccuracy || 87}%`,
            icon: <Security sx={{ fontSize: compact ? 30 : 40 }} />,
            color: '#607d8b',
            gradient: 'linear-gradient(135deg, #eceff1, #cfd8dc)',
            route: '/admin/stats',
            trend: { direction: 'up', value: '+2%' }
        }
    ];

    // Show only first 4 cards in compact mode
    const displayStats = compact ? statsConfig.slice(0, 4) : statsConfig;

    if (loading) {
        return (
            <Grid container spacing={compact ? 2 : 3}>
                {displayStats.map((_, index) => (
                    <Grid item xs={12} sm={6} md={compact ? 6 : 4} key={index}>
                        <Card sx={{ borderRadius: 3, height: compact ? 120 : 140 }}>
                            <CardContent sx={{ textAlign: 'center', p: compact ? 2 : 3 }}>
                                <Skeleton variant="circular" width={compact ? 40 : 60} height={compact ? 40 : 60} sx={{ mx: 'auto', mb: 2 }} />
                                <Skeleton variant="text" width="60%" height={compact ? 30 : 40} sx={{ mx: 'auto', mb: 1 }} />
                                <Skeleton variant="text" width="80%" height={20} sx={{ mx: 'auto' }} />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    }

    return (
        <Grid container spacing={compact ? 2 : 3}>
            {displayStats.map((stat) => (
                <Grid item xs={12} sm={6} md={compact ? 6 : 4} key={stat.key}>
                    <Card
                        elevation={3}
                        sx={{
                            borderRadius: 3,
                            border: `2px solid ${stat.color}`,
                            background: stat.gradient,
                            cursor: onCardClick ? 'pointer' : 'default',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            height: compact ? 120 : 140,
                            '&:hover': onCardClick ? {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 8px 25px ${stat.color}20`
                            } : {}
                        }}
                        onClick={() => onCardClick && onCardClick(stat.route)}
                    >
                        <CardContent sx={{
                            textAlign: 'center',
                            p: compact ? 2 : 3,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            position: 'relative'
                        }}>
                            {/* Action Buttons */}
                            <Box sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                display: 'flex',
                                gap: 0.5
                            }}>
                                {onRefresh && (
                                    <Tooltip title="Refresh">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onRefresh(stat.key);
                                            }}
                                            sx={{
                                                color: stat.color,
                                                opacity: 0.7,
                                                '&:hover': {
                                                    opacity: 1,
                                                    transform: 'rotate(180deg)',
                                                    background: `${stat.color}15`
                                                },
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            <Refresh fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {onCardClick && (
                                    <Tooltip title="View Details">
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color: stat.color,
                                                opacity: 0.7,
                                                '&:hover': {
                                                    opacity: 1,
                                                    background: `${stat.color}15`
                                                }
                                            }}
                                        >
                                            <Visibility fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {/* Icon */}
                            <Box sx={{ color: stat.color, mb: compact ? 1 : 2 }}>
                                {stat.icon}
                            </Box>

                            {/* Value */}
                            <Typography
                                variant={compact ? "h4" : "h3"}
                                fontWeight={800}
                                sx={{
                                    color: stat.color,
                                    mb: compact ? 0.5 : 1,
                                    lineHeight: 1
                                }}
                            >
                                {stat.value}
                            </Typography>

                            {/* Title */}
                            <Typography
                                variant={compact ? "body2" : "h6"}
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                            >
                                {stat.title}
                            </Typography>

                            {/* Trend Indicator */}
                            {!compact && stat.trend && (
                                <Box sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    background: `${stat.color}15`,
                                    px: 1,
                                    py: 0.3,
                                    borderRadius: 1
                                }}>
                                    <TrendingUp sx={{ fontSize: 12, color: stat.color }} />
                                    <Typography variant="caption" sx={{ color: stat.color, fontWeight: 600 }}>
                                        {stat.trend.value}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

export default AdminStatsCards;
